import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import FooterSection from './FooterSection';

const API_BASE_URL = 'https://care-api.equily.ng';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [commentSuccess, setCommentSuccess] = useState('');

  const isNumericSlug = useMemo(() => /^\d+$/.test(slug || ''), [slug]);
  const comments = Array.isArray(blog?.comments) ? blog.comments : [];
  const currentPageUrl =
    typeof window !== 'undefined' ? window.location.href : '';
  const encodedPageUrl = encodeURIComponent(currentPageUrl);
  const encodedTitle = encodeURIComponent(blog?.title || 'CARE Blog');

  const shareLinks = [
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedPageUrl}`,
      Icon: Facebook,
    },
    {
      name: 'Twitter',
      href: `https://twitter.com/intent/tweet?url=${encodedPageUrl}&text=${encodedTitle}`,
      Icon: Twitter,
    },
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedPageUrl}`,
      Icon: Linkedin,
    },
    {
      name: 'Instagram',
      href: `https://www.instagram.com/?url=${encodedPageUrl}`,
      Icon: Instagram,
    },
  ];

  useEffect(() => {
    if (!slug) return;

    const controller = new AbortController();

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError('');

        if (isNumericSlug) {
          const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`, {
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error('Unable to fetch blog post.');
          }

          const result = await response.json();
          setBlog(result?.data || null);
          return;
        }

        const listResponse = await fetch(
          `${API_BASE_URL}/api/blogs?slug=${encodeURIComponent(slug)}`,
          {
            signal: controller.signal,
          }
        );

        if (!listResponse.ok) {
          throw new Error('Unable to fetch blog post.');
        }

        const listResult = await listResponse.json();
        const matchedBlog = Array.isArray(listResult?.data)
          ? listResult.data[0]
          : null;

        if (!matchedBlog) {
          throw new Error('Blog not found.');
        }

        const detailResponse = await fetch(
          `${API_BASE_URL}/api/blogs/${matchedBlog.id}`,
          {
            signal: controller.signal,
          }
        );

        if (!detailResponse.ok) {
          throw new Error('Unable to fetch blog post details.');
        }

        const detailResult = await detailResponse.json();
        setBlog(detailResult?.data || matchedBlog);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Something went wrong while fetching blog.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();

    return () => controller.abort();
  }, [slug, isNumericSlug]);

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    const trimmedComment = commentText.trim();
    const trimmedFullName = fullName.trim();
    if (!trimmedFullName) {
      setCommentError('Please enter your full name.');
      setCommentSuccess('');
      return;
    }
    if (!trimmedComment) {
      setCommentError('Please enter a comment.');
      setCommentSuccess('');
      return;
    }

    if (!blog?.id) {
      setCommentError('Blog details are not ready yet. Please try again.');
      setCommentSuccess('');
      return;
    }

    try {
      setIsSubmittingComment(true);
      setCommentError('');
      setCommentSuccess('');

      const response = await fetch(`${API_BASE_URL}/api/blog_comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: trimmedFullName,
          comment: trimmedComment,
          blog_id: blog.id,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage =
          result?.message || 'Unable to submit comment. Please try again.';
        throw new Error(errorMessage);
      }

      setCommentText('');
      setFullName('');
      setCommentSuccess('Comment submitted successfully.');

      const refreshedResponse = await fetch(`${API_BASE_URL}/api/blogs/${blog.id}`);
      if (refreshedResponse.ok) {
        const refreshedResult = await refreshedResponse.json();
        setBlog(refreshedResult?.data || blog);
      }
    } catch (submitError) {
      setCommentError(
        submitError.message || 'Something went wrong while submitting comment.'
      );
      setCommentSuccess('');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <>
      <section className='bg-white min-h-screen py-12 px-4 sm:px-8 md:px-20'>
        <div className='max-w-4xl mx-auto'>
          <Link
            to='/blog'
            className='inline-flex items-center text-orange-600 hover:text-orange-700 font-raleway mb-6'
          >
            ← Back to Blog
          </Link>

          {loading && (
            <p className='font-raleway text-gray-600'>Loading blog post...</p>
          )}

          {!loading && error && (
            <p className='font-raleway text-red-600'>{error}</p>
          )}

          {!loading && !error && blog && (
            <article>
              <h1 className='text-3xl sm:text-4xl font-Anton text-orange-900 mb-3'>
                {blog.title}
              </h1>

              <p className='font-raleway text-gray-500 mb-6'>
                {formatDate(blog.published_at || blog.created_at)}
              </p>

              <div className='mb-8'>
                <p className='font-raleway text-gray-700 mb-3'>Share this post</p>
                <div className='flex items-center gap-3'>
                  {shareLinks.map(({ name, href, Icon }) => (
                    <a
                      key={name}
                      href={href}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={`Share on ${name}`}
                      className='w-10 h-10 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center transition'
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>

              {blog.file_url && (
                <img
                  src={blog.file_url}
                  alt={blog.title || 'Blog'}
                  className='w-full max-h-[450px] object-cover rounded-lg mb-8'
                />
              )}

              {blog.content ? (
                <div
                  className='font-raleway text-gray-800 leading-8 whitespace-pre-wrap'
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              ) : (
                <p className='font-raleway text-gray-700'>No content available.</p>
              )}

              <div className='mt-12 border-t border-gray-200 pt-8'>
                <h2 className='text-2xl font-Anton text-orange-900 mb-5'>
                  Add a Comment
                </h2>

                <form onSubmit={handleCommentSubmit} className='space-y-3'>
                  <input
                    type='text'
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    placeholder='Full Name'
                    className='w-full border border-gray-300 rounded-md p-3 font-raleway focus:outline-none focus:ring-2 focus:ring-orange-500'
                  />
                  <textarea
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                    placeholder='Write your comment...'
                    rows={4}
                    className='w-full border border-gray-300 rounded-md p-3 font-raleway focus:outline-none focus:ring-2 focus:ring-orange-500'
                  />

                  {commentError && (
                    <p className='font-raleway text-red-600 text-sm'>{commentError}</p>
                  )}

                  {commentSuccess && (
                    <p className='font-raleway text-green-600 text-sm'>
                      {commentSuccess}
                    </p>
                  )}

                  <button
                    type='submit'
                    disabled={isSubmittingComment}
                    className='bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-raleway text-sm px-5 py-2 rounded-md'
                  >
                    {isSubmittingComment ? 'Submitting...' : 'Post Comment'}
                  </button>
                </form>
              </div>

              <div className='mt-12 border-t border-gray-200 pt-8'>
                <h2 className='text-2xl font-Anton text-orange-900 mb-5'>
                  Comments ({comments.length})
                </h2>

                {comments.length === 0 && (
                  <p className='font-raleway text-gray-600'>No comments yet.</p>
                )}

                {comments.length > 0 && (
                  <div className='space-y-4'>
                    {comments.map((commentItem) => {
                      const fullName = commentItem.full_name;
                     
                      const authorName = fullName || 'Anonymous';

                      return (
                        <div
                          key={commentItem.id}
                          className='bg-gray-50 border border-gray-200 rounded-md p-4'
                        >
                          <div className='flex items-center justify-between gap-3 mb-2'>
                            <p className='font-raleway font-semibold text-orange-900'>
                              {authorName}
                            </p>
                            <p className='font-raleway text-sm text-gray-500'>
                              {formatDate(commentItem.created_at)}
                            </p>
                          </div>

                          <p className='font-raleway text-gray-800'>
                            {commentItem.comment || 'No comment text.'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </article>
          )}
        </div>
      </section>
      <FooterSection />
    </>
  );
}

export default BlogDetail;
